import React, { useEffect, useState } from 'react';
import useOnboardingSummary from '../../../hooks/useOnboardingSummary';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchStream } from '../../../services/profileService';
import type { OnboardingSummary } from '../../../types/onboarding';
import { AiOutlineUser } from 'react-icons/ai';
import { Calendar, GoogleDocs, Microphone, Suitcase } from 'iconoir-react';
import GolfIcon from '../../../assets/images/golf.svg';
import LinkedInIcon from '../../../assets/images/linkedin.svg';

interface IdentitySectionProps {
  section: OnboardingSummary;
}

const IdentitySection = (props: IdentitySectionProps) => {
  const { section } = props;

  const { user } = useAuth();
  const {
    age,
    gender,
    resume,
    linkedin,
    briefProfessionalBackground,
    elevatorPitch,
    coreStrengths,
    coreStrengths2,
    coreStrengths3,
  } = useOnboardingSummary({ section });

  const [resumeUrl, setResumeUrl] = useState<string | null>(
    (resume?.url as string) || null
  );

  useEffect(() => {
    async function getResumeUrl() {
      if (!resume?.url) {
        return null;
      }
      const response = await fetchStream({ url: resume?.url as string });
      if (response) {
        setResumeUrl(response as unknown as string);
      }
    }

    getResumeUrl();
  }, []);

  return (
    <div>
      <div className='border-purple-25 border-b pb-6 mb-6'>
        <div className='flex sm:items-center justify-between'>
          <div>
            <div className='sm:flex items-center gap-6'>
              <h2 className='font-heading !font-medium text-primary-400 sm:mb-0 mb-3'>
                {user?.first_name} {user?.last_name}
              </h2>
              <div className='lg:flex gap-8'>
                <div className='flex flex-col gap-3 lg:mb-0 mb-3'>
                  <div className='flex items-center gap-2'>
                    <p className='text-body-400 font-medium min-w-[5.3125rem] mb-0'>
                      Gender:
                    </p>
                    <p className='font-medium flex items-center gap-2 min-w-[7rem] mb-0 capitalize'>
                      <AiOutlineUser size={20} /> {gender}
                    </p>
                  </div>
                  <div className='flex items-center gap-2'>
                    <p className='text-body-400 font-medium min-w-[5.3125rem] mb-0'>
                      Age:
                    </p>
                    <p className='font-medium flex items-center gap-2 min-w-[7rem] mb-0'>
                      <Calendar className='w-5 h-5' /> {age} Years
                    </p>
                  </div>
                </div>
                <div className='flex flex-col gap-3'>
                  {resumeUrl && (
                    <div className='flex items-center gap-2'>
                      <p className='text-body-400 font-medium min-w-[9rem] mb-0'>
                        Resume/Profile:
                      </p>
                      <p className='font-medium flex items-center gap-2 min-w-[7rem] mb-0'>
                        <GoogleDocs className='w-6 h-6' />
                        <a
                          href={resumeUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='!text-black'
                        >
                          Resume
                        </a>
                      </p>
                    </div>
                  )}
                  {linkedin && (
                    <div className='flex items-center gap-2'>
                      <p className='text-body-400 font-medium min-w-[9rem] mb-0'>
                        LinkedIn profile link:
                      </p>
                      <p className='font-medium flex items-center gap-2 min-w-[7rem] mb-0'>
                        <img
                          className='w-6 h-6'
                          src={LinkedInIcon}
                          alt='LinkedIn profile link'
                        />
                        <a
                          href={linkedin}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='!text-black'
                        >
                          LinkedIn link
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {briefProfessionalBackground && (
          <>
            <div className='font-bold text-body-400 mb-4 flex items-center gap-2 mt-6'>
              <Suitcase className='w-[1.375rem] h-[1.375rem]' /> Brief
              professional background:
            </div>
            <div>{briefProfessionalBackground}</div>
          </>
        )}
      </div>
      <div>
        <div className='font-bold text-body-400 mb-4 flex items-center gap-2'>
          <Microphone className='w-[1.375rem] h-[1.375rem]' /> Elevator pitch:
        </div>
        <div className='mb-6'>{elevatorPitch}</div>

        <div className='font-bold text-body-400 mb-4 flex items-center gap-2'>
          <img className='w-6 h-6' src={GolfIcon} alt='Core strengths' />
          Core strengths:
        </div>
        <div className='mb-6 flex flex-wrap gap-4'>
          <div className='px-5 py-2 bg-green-700 text-white font-medium rounded-xl'>
            {coreStrengths}
          </div>
          <div className='px-5 py-2 bg-green-700 text-white font-medium rounded-xl'>
            {coreStrengths2}
          </div>
          <div className='px-5 py-2 bg-green-700 text-white font-medium rounded-xl'>
            {coreStrengths3}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdentitySection;
